/**
 * Linkable Field Type
 *
 * Provides custom column renderer for linkable cells that navigate to URLs.
 * Supports both read-only and editable (inline edit with link in read mode).
 */

import { useCallback } from 'react';
import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { PencilSimple } from '@phosphor-icons/react';
import { CellInput } from 'features/misc/table';
import { SharedEditableCell, LinkableCell, useEditableContextOptional } from '../../components';
import { DotsMenuButton } from 'features/misc/table/components/DotsMenuButton';
import { EditableCellRow, EditableCellContentArea, DotsMenuButtonWrapper } from '../../styles';
import type { ColumnRenderer, RenderOptions, CustomColumn } from '../../types';
import { clearEditOnSuccess } from '../inlineEditUtils';
import { getCellStringValue } from './cellValue';

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Linkable read-only renderer - renders a cell with a link to a URL
 * Requires linkConfig with urlBuilder function
 */
export const linkableReadOnlyRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  if (!col.linkConfig) throw new Error(`linkConfig required for linkable column: ${col.id}`);

  const linkConfig = col.linkConfig;
  return {
    cell: (context: CellContext<T, unknown>) => {
      // Use shared getCellStringValue to respect accessorFn (important for custom fields)
      const value = getCellStringValue(context, col.id);
      return (
        <LinkableCell
          value={value}
          url={linkConfig.urlBuilder(context.row.original)}
          showLinkIcon={linkConfig.showLinkIcon}
          newTab={linkConfig.newTab}
          isBold={col.primaryColumn}
        />
      );
    },
    tooltipRender: (cell: CellContext<T, unknown>) => {
      // Use shared getCellStringValue to respect accessorFn
      return getCellStringValue(cell, col.id);
    },
  };
};

// ============================================================================
// Editable Column Renderer
// ============================================================================

/**
 * Linkable editable renderer - link in read mode, inline text input in edit mode.
 * Uses same editable state pattern as text; edit is entered via dots menu (clicking link navigates).
 */
export const linkableEditableRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  if (!col.linkConfig) throw new Error(`linkConfig required for linkable column: ${col.id}`);

  const linkConfig = col.linkConfig;
  const editableProp = col.editable;

  return {
    cell: (context: CellContext<T, unknown>) => {
      const value = getCellStringValue(context, col.id);
      const isRowEditable =
        typeof editableProp === 'function' ? editableProp(context) : editableProp === true;

      if (!isRowEditable) {
        return (
          <LinkableCell
            value={value}
            url={linkConfig.urlBuilder(context.row.original)}
            showLinkIcon={linkConfig.showLinkIcon}
            newTab={linkConfig.newTab}
            isBold={col.primaryColumn}
          />
        );
      }

      return (
        <LinkableEditableCell context={context} col={col} value={value} linkConfig={linkConfig} />
      );
    },
    meta: {
      editable: true,
    },
    tooltipRender: (cell: CellContext<T, unknown>) => {
      return getCellStringValue(cell, col.id);
    },
  };
};

const LinkableEditableCell = <T extends { id: number | string }>({
  context,
  col,
  value,
  linkConfig,
}: {
  context: CellContext<T, unknown>;
  col: CustomColumn<T>;
  value: string;
  linkConfig: NonNullable<CustomColumn<T>['linkConfig']>;
}) => {
  const editableCtx = useEditableContextOptional();
  const setEditableState = editableCtx?.setEditableState ?? ((): void => undefined);
  const clearEdit = useCallback(
    () => setEditableState({ rowIndex: null, columnId: null }),
    [setEditableState]
  );
  const editCell = useCallback(
    () => setEditableState({ rowIndex: context.row.index, columnId: context.column.id }),
    [setEditableState, context.row.index, context.column.id]
  );

  const readModeEl = (
    <LinkableCell
      value={value}
      url={linkConfig.urlBuilder(context.row.original)}
      showLinkIcon={linkConfig.showLinkIcon}
      newTab={linkConfig.newTab}
      isBold={col.primaryColumn}
    />
  );

  const menuOptions = [
    {
      label: 'Edit',
      icon: <PencilSimple data-testid="pencil_simple_icon" size={16} />,
      onClick: editCell,
    },
  ];

  return (
    <EditableCellRow>
      <EditableCellContentArea>
        <SharedEditableCell
          {...context}
          meta={{ editable: true }}
          tooltipTitle={value}
          readModeEl={readModeEl}
          hideActions
        >
          <CellInput
            value={value || ''}
            onChange={newValue => {
              if (col.onUpdate) {
                const result = col.onUpdate(context.row.original, {
                  [col.id]: newValue,
                } as Partial<T>);
                clearEditOnSuccess(result, clearEdit);
              } else {
                clearEdit();
              }
            }}
          />
        </SharedEditableCell>
      </EditableCellContentArea>
      <DotsMenuButtonWrapper>
        <DotsMenuButton menuOptions={menuOptions} />
      </DotsMenuButtonWrapper>
    </EditableCellRow>
  );
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const linkableReadOnly: ColumnRenderer = linkableReadOnlyRenderer;

/** Editable renderer - link in read mode, inline edit via CellInput */
export const linkableEditable: ColumnRenderer = linkableEditableRenderer;
